import React, { useMemo, useState } from 'react';
import { LayersIcon, PlusIcon, SearchIcon, Trash2Icon, ZapIcon } from 'lucide-react';
import { FormConfig, FormData, VisibilityRule } from '../../types';
import { conditionMet, createGroupId, createRuleId, fieldCatalog } from '../../utils/config';
import { formatValue } from '../../utils/format';
import { RuleRow } from './RuleRow';
interface RulesTabProps {
  config: FormConfig;
  values: FormData;
  onChange: (config: FormConfig) => void;
}
const ALL = '__all';
export function RulesTab({
  config,
  values,
  onChange
}: RulesTabProps) {
  const [selectedGroup, setSelectedGroup] = useState<string>(ALL);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const fieldOptions = useMemo(() => Object.values(fieldCatalog).map(f => ({
    value: f.id,
    label: f.label
  })).sort((a, b) => a.label.localeCompare(b.label)), []);
  const groups = useMemo(() => config.ruleGroups.map(group => {
    const rules = config.rules.filter(r => r.groupId === group.id);
    return {
      ...group,
      rules,
      activeCount: rules.filter(r => r.enabled && conditionMet(r, values)).length,
      watchedFieldIds: Array.from(new Set(rules.map(r => r.conditionFieldId))).filter(Boolean)
    };
  }), [config.ruleGroups, config.rules, values]);
  const q = query.trim().toLowerCase();
  const matchesQuery = (rule: VisibilityRule, groupName: string) => {
    if (!q) return true;
    const target = fieldCatalog[rule.targetFieldId]?.label ?? '';
    const condition = fieldCatalog[rule.conditionFieldId]?.label ?? '';
    return groupName.toLowerCase().includes(q) || target.toLowerCase().includes(q) || condition.toLowerCase().includes(q) || rule.conditionValue.toLowerCase().includes(q);
  };
  const visibleGroups = groups.filter(g => selectedGroup === ALL || g.id === selectedGroup).map(g => ({
    ...g,
    rules: g.rules.filter(r => matchesQuery(r, g.name))
  })).filter(g => g.rules.length > 0 || !q && selectedGroup === g.id);
  const totalActive = groups.reduce((sum, g) => sum + g.activeCount, 0);
  const updateRule = (id: string, patch: Partial<VisibilityRule>) => {
    onChange({
      ...config,
      rules: config.rules.map(r => r.id === id ? {
        ...r,
        ...patch
      } : r)
    });
  };
  const deleteRule = (id: string) => {
    onChange({
      ...config,
      rules: config.rules.filter(r => r.id !== id)
    });
    setExpandedRuleId(prev => prev === id ? null : prev);
  };
  const renameGroup = (groupId: string, name: string) => {
    onChange({
      ...config,
      ruleGroups: config.ruleGroups.map(g => g.id === groupId ? {
        ...g,
        name
      } : g)
    });
  };
  const deleteGroup = (groupId: string) => {
    onChange({
      ...config,
      ruleGroups: config.ruleGroups.filter(g => g.id !== groupId),
      rules: config.rules.filter(r => r.groupId !== groupId)
    });
    setSelectedGroup(ALL);
  };

  /** New rules inherit the group's existing condition (first rule wins). */
  const addRule = (groupId: string) => {
    const template = config.rules.find(r => r.groupId === groupId);
    const rule: VisibilityRule = {
      id: createRuleId(),
      groupId,
      enabled: true,
      targetFieldId: fieldOptions[0]?.value ?? '',
      action: template?.action ?? 'hide',
      conditionFieldId: template?.conditionFieldId ?? fieldOptions[0]?.value ?? '',
      operator: template?.operator ?? 'isEmpty',
      conditionValue: template?.conditionValue ?? ''
    };
    onChange({
      ...config,
      rules: [...config.rules, rule]
    });
    setSelectedGroup(groupId);
    setExpandedRuleId(rule.id);
    setQuery('');
  };
  const addGroup = () => {
    const group = {
      id: createGroupId(),
      name: `Rule Group ${config.ruleGroups.length + 1}`
    };
    onChange({
      ...config,
      ruleGroups: [...config.ruleGroups, group]
    });
    setSelectedGroup(group.id);
    setQuery('');
  };
  return <div className="space-y-4" data-id="element-95" data-mp="t6cut31wcoiq">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-96" data-mp="o9mhgnhh8710">
        <div data-id="element-97" data-mp="k7hmnlfal4cs">
          <p className="text-sm font-semibold text-slate-800" data-id="element-98" data-mp="k79cw2eguf5t">
            {config.rules.length} {config.rules.length === 1 ? 'rule' : 'rules'} in{' '}
            {groups.length} {groups.length === 1 ? 'group' : 'groups'}
          </p>
          <p className="text-xs text-slate-500" data-id="element-99" data-mp="1byfgwtxb4x0d">
            {totalActive} currently matching this record. Group related rules and name them however
            your team refers to them.
          </p>
        </div>
        <div className="relative w-full sm:w-56" data-id="element-100" data-mp="134itzcwbjgjw">
          <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-101" data-mp="y8byyf5gnpgq" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search rules…" aria-label="Search rules" className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-102" data-mp="1vn68g9gt7xc0" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)]" data-id="element-103" data-mp="283z15h0veyzv">
        {/* Group navigator */}
        <div className="space-y-1.5" data-id="element-104" data-mp="2c510dxusfb1u">
          <button type="button" onClick={() => setSelectedGroup(ALL)} aria-current={selectedGroup === ALL} className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${selectedGroup === ALL ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`} data-id="element-105" data-mp="2h6qejmdb44u5">
            <LayersIcon className="h-4 w-4 shrink-0" data-id="element-106" data-mp="2zumyehzfbk89" />
            <span className="min-w-0 flex-1 truncate text-xs font-semibold" data-id="element-107" data-mp="34v14huhgsk3n">All groups</span>
            <span className="shrink-0 text-[11px] font-bold tabular-nums text-slate-400" data-id="element-108" data-mp="14srxmfptsxox">
              {config.rules.length}
            </span>
          </button>

          {groups.map(group => <button key={group.id} type="button" onClick={() => setSelectedGroup(group.id)} aria-current={selectedGroup === group.id} className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${selectedGroup === group.id ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`} data-id="element-109" data-mp="1ejmibfnfbp89">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${group.activeCount > 0 ? 'bg-blue-500' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-110" data-mp="1skffk8540yf2" />
              <span className="min-w-0 flex-1 truncate text-xs font-semibold" data-id="element-111" data-mp="3uclkzgfg0zkt">{group.name}</span>
              <span className="shrink-0 text-[11px] font-bold tabular-nums text-slate-400" data-id="element-112" data-mp="29ujcd92lm2r5">
                {group.rules.length}
              </span>
            </button>)}

          <button type="button" onClick={addGroup} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600" data-id="element-113" data-mp="20zmsnml957p1">
            <PlusIcon className="h-3.5 w-3.5" data-id="element-114" data-mp="1vyyumgylvqlb" />
            New group
          </button>
        </div>

        {/* Rules in the selected group(s) */}
        <div className="min-w-0 space-y-5" data-id="element-115" data-mp="1m23yu5tpw4aq">
          {visibleGroups.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center" data-id="element-116" data-mp="3ces2dajsw6i5">
              <ZapIcon className="mx-auto mb-2 h-5 w-5 text-slate-300" data-id="element-117" data-mp="3ghevdes7jxgx" />
              <p className="text-sm text-slate-400" data-id="element-118" data-mp="247jsx8hki2u5">
                {q ? `No rules match “${query}”.` : 'No rule groups yet.'}
              </p>
            </div>}

          {visibleGroups.map(group => <section key={group.id} data-id="element-119" data-mp="1uh8cimgp616d">
              <div className="mb-2 flex items-center gap-2" data-id="element-120" data-mp="1ugfbm24f65w5">
                <input value={group.name} aria-label="Rule group name" onChange={e => renameGroup(group.id, e.target.value)} className="min-w-0 flex-1 rounded-md border border-transparent px-1.5 py-1 text-sm font-bold text-slate-800 outline-none transition-colors hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-121" data-mp="1pj6tg1j9u1uw" />
                {group.activeCount > 0 && <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700" data-id="element-122" data-mp="35wwybai7su5l">
                    {group.activeCount} active
                  </span>}
                <button type="button" onClick={() => addRule(group.id)} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-blue-600" data-id="element-123" data-mp="39fmc44lspxam">
                  <PlusIcon className="h-3 w-3" data-id="element-124" data-mp="3n5fzsjlkebir" />
                  Add rule
                </button>
                <button type="button" onClick={() => deleteGroup(group.id)} aria-label={`Delete ${group.name}`} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-125" data-mp="2qhryfd9q2xlw">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-126" data-mp="eqxpscc2clwi" />
                </button>
              </div>

              {group.watchedFieldIds.length > 0 && <div className="mb-2 flex flex-wrap items-center gap-1.5" data-id="element-127" data-mp="jrlprni9sd2v">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-128" data-mp="1b41ria422jdg">
                    Watches
                  </span>
                  {group.watchedFieldIds.slice(0, 3).map(id => {
              const field = fieldCatalog[id];
              if (!field) return null;
              return <span key={id} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600" data-id="element-129" data-mp="s68nf4hp43eh">
                        {field.label}:{' '}
                        <span className="font-bold" data-id="element-130" data-mp="115vpztm5vf94">
                          {formatValue(field.type, values[id]) || 'Empty'}
                        </span>
                      </span>;
            })}
                  {group.watchedFieldIds.length > 3 && <span className="text-[11px] text-slate-400" data-id="element-131" data-mp="2hk3yika3jo1r">
                      +{group.watchedFieldIds.length - 3} more
                    </span>}
                </div>}

              {group.rules.length === 0 ? <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400" data-id="element-132" data-mp="2ms3j7zhylqiz">
                  No rules in this group yet — add one to get started.
                </p> : <ul className="space-y-1.5" data-id="element-133" data-mp="28ym9f6f2rs65">
                  {group.rules.map(rule => <RuleRow key={rule.id} rule={rule} values={values} fieldOptions={fieldOptions} expanded={expandedRuleId === rule.id} onToggleExpand={() => setExpandedRuleId(prev => prev === rule.id ? null : rule.id)} onUpdate={patch => updateRule(rule.id, patch)} onDelete={() => deleteRule(rule.id)} data-id="element-134" data-mp="23yq3v2qqbsoh" />)}
                </ul>}
            </section>)}
        </div>
      </div>
    </div>;
}